import { colorStarPours, ColorModel, FLASK_CAPACITY, solveColors, type ColorRules } from "@nano/puzzle-core";
import { z } from "zod";
import { LAB_COLOR_BY_ID, LAB_MIXES } from "./palette.ts";

export const COLOR_HOUSE_ID = "colors" as const;

const colorIdSchema = z.string().refine((id) => LAB_COLOR_BY_ID.has(id), "unknown lab colour");

export const vialSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,15}$/),
    capacity: z.number().int().min(2).max(6),
    layers: z.array(colorIdSchema).max(6),
    hidden: z.number().int().min(0).max(5).optional(),
    seal: colorIdSchema.optional(),
    flask: z.literal(true).optional(),
  })
  .strict();

const mixSchema = z.object({ a: colorIdSchema, b: colorIdSchema, result: colorIdSchema }).strict();

export const colorRulesSchema = z
  .object({
    vials: z.array(vialSchema).min(3).max(12),
    mixes: z.array(mixSchema).min(1).max(6).optional(),
    targets: z.record(colorIdSchema, z.number().int().min(0).max(6)).optional(),
  })
  .strict();

export const colorLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(COLOR_HOUSE_ID),
    contentVersion: z.string().regex(/^colors-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Fewest pours, proven by the solver when the level was frozen. */
    optimalPours: z.number().int().min(1),
    threeStarPours: z.number().int().min(1),
    twoStarPours: z.number().int().min(1),
    seed: z.number().int().nonnegative().optional(),
    rules: colorRulesSchema,
  })
  .strict();

export type ColorLevel = z.infer<typeof colorLevelSchema>;

export const colorPackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(COLOR_HOUSE_ID),
    contentVersion: z.string().regex(/^colors-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(colorLevelSchema).length(10),
  })
  .strict();

export type ColorPack = z.infer<typeof colorPackSchema>;

export function toColorRules(level: ColorLevel): ColorRules {
  return level.rules as ColorRules;
}

/** Checks a level against the rules and the solver: problems, or an empty list. */
export function validateColorLevel(level: ColorLevel, options: { solve?: boolean } = {}): string[] {
  const problems: string[] = [];
  let model: ColorModel;
  try {
    model = new ColorModel(toColorRules(level));
  } catch (error) {
    return [`level ${level.id}: ${(error as Error).message}`];
  }
  const vials = level.rules.vials;
  for (const [color, total] of model.totals) {
    if (!vials.some((vial) => !vial.flask && vial.capacity === total)) problems.push(`level ${level.id}: no vial fits exactly the ${total} ${color} units`);
  }
  const flasks = vials.filter((vial) => vial.flask);
  if (flasks.length > 1) problems.push(`level ${level.id}: at most one flask`);
  if (flasks.some((flask) => flask.capacity !== FLASK_CAPACITY)) problems.push(`level ${level.id}: the flask holds ${FLASK_CAPACITY}`);
  // Players learn one recipe card; a level with a flask always uses all of it.
  const recipes = JSON.stringify(level.rules.mixes ?? []);
  if (flasks.length > 0 && recipes !== JSON.stringify(LAB_MIXES)) problems.push(`level ${level.id}: the flask must use the standard recipes`);
  if (flasks.length === 0 && level.rules.mixes) problems.push(`level ${level.id}: recipes without a flask`);
  const stars = colorStarPours(level.optimalPours);
  if (stars.threeStarPours !== level.threeStarPours || stars.twoStarPours !== level.twoStarPours) {
    problems.push(`level ${level.id}: star thresholds should be ${stars.threeStarPours} and ${stars.twoStarPours}`);
  }
  if (options.solve !== false) {
    const solved = solveColors(model, { maxNodes: 2_000_000 });
    if (solved.status !== "solved") problems.push(`level ${level.id}: solver says ${solved.status}`);
    else if (solved.optimalPours !== level.optimalPours) problems.push(`level ${level.id}: optimal is ${solved.optimalPours}, data says ${level.optimalPours}`);
  }
  return problems;
}
